/**
 * (c) 2021, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { sanityLanguageId } from "../../common/sanity";
import { Toolkit, ToolkitTopic, ToolkitTopicEntry } from "./model";
import { baseUrl } from "../../base";

export const fetchReferenceToolkit = async (
  languageId: string
): Promise<Toolkit> => {
  for (const candidate of contentLanguages(languageId)) {
    const toolkit = await fetchLocalToolkit(candidate);
    if (toolkit) {
      return toolkit;
    }
  }
  throw new Error(
    "Die deutschen Referenzinhalte fehlen. Bitte 'make lessons' ausfuehren."
  );
};

/**
 * Sprachen, in denen Lerninhalte gesucht werden, in dieser Reihenfolge.
 *
 * Erst die eingestellte Sprache, dann Englisch wie in der uebrigen
 * Oberflaeche, zuletzt Deutsch: die Kapitel unter lessons/ entstehen auf
 * Deutsch, und eine Uebersetzung kann einmal fehlen.
 */
export const contentLanguages = (languageId: string): string[] => {
  const result: string[] = [];
  for (const candidate of [sanityLanguageId(languageId), "en", "de"]) {
    if (!result.includes(candidate)) {
      result.push(candidate);
    }
  }
  return result;
};

const referenceContentUrl = (path: string): string =>
  `${baseUrl}content/reference/${path}`;

const fetchLocalToolkit = async (
  languageId: string
): Promise<Toolkit | undefined> => {
  const response = await fetch(
    referenceContentUrl(`languages/${encodeURIComponent(languageId)}.json`)
  );
  if (response.status === 404) {
    return undefined;
  }
  if (!response.ok) {
    throw new Error(`Error fetching local reference: ${response.status}`);
  }
  return adaptContent([await response.json()]);
};

/**
 * Ein Eintrag der Themenliste: entweder eine Zwischenueberschrift oder ein
 * Thema.
 */
export type TopicListItem =
  | { kind: "group"; name: string }
  | { kind: "topic"; topic: ToolkitTopic };

/**
 * Setzt vor jedes Thema, dessen Block sich vom vorigen unterscheidet, eine
 * Zwischenueberschrift. Themen ohne Block bekommen keine und beenden auch
 * keinen: so bleiben angehaengte Inhalte ohne Blockangabe unter dem letzten
 * Block stehen, statt eine leere Ueberschrift zu erzeugen.
 */
export const withGroupHeadings = (topics: ToolkitTopic[]): TopicListItem[] => {
  const items: TopicListItem[] = [];
  let current: string | undefined;
  for (const topic of topics) {
    if (topic.group && topic.group !== current) {
      items.push({ kind: "group", name: topic.group });
      current = topic.group;
    }
    items.push({ kind: "topic", topic });
  }
  return items;
};

export const localReferenceImageUrl = (imageRef: string): string => {
  const match = /^image-(.+)-(\d+x\d+)-([a-z0-9]+)$/i.exec(imageRef);
  if (!match) {
    throw new Error(`Invalid Sanity image reference: ${imageRef}`);
  }
  return referenceContentUrl(`assets/${match[1]}-${match[2]}.${match[3]}`);
};

export const getTopicAndEntry = (
  toolkit: Toolkit,
  topicOrEntryId: string | undefined
): [ToolkitTopic | undefined, ToolkitTopicEntry | undefined] => {
  const topic = toolkit.contents?.find(
    (t) => t.slug.current === topicOrEntryId
  );
  if (topic) {
    return [topic, undefined];
  }
  const entry = toolkit.contents
    ?.flatMap((topic) => topic.contents ?? [])
    .find((entry) => entry.slug.current === topicOrEntryId);
  if (!entry) {
    return [undefined, undefined];
  }
  return [entry.parent, entry];
};

const adaptContent = (result: any): Toolkit | undefined => {
  const toolkits = result as Toolkit[];
  if (toolkits.length === 0) {
    return undefined;
  }
  if (toolkits.length > 1) {
    throw new Error("Unexpected results");
  }
  // Add topic entry parent for toolkit navigation.
  const toolkit = toolkits[0];
  toolkit.contents?.forEach((topic) => {
    topic.contents?.forEach((entry) => {
      entry.parent = topic;
    });
  });
  return toolkit;
};
